import { Component, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, Attendance, AttendanceService, AttendanceStatusEnum, CacheKeyEnum, Channel, ChannelService, ConfirmationComponent, Department, DepartmentService, isHybridChannel, Pager, SidebarService, Team, TeamService, User, UserService } from 'lib-trend-core';
import { Observable, Subject, debounceTime, distinctUntilChanged, forkJoin, switchMap, takeUntil } from 'rxjs';
import { AttendanceHistoryComponent, AttendanceHistoryData } from './attendance-history/attendance-history.component';
import { AssignedUserComponent } from './attendance-panel/attendance-panel-components/assigned-user/assigned-user.component';

@Component({
    templateUrl: 'attendance.v2.component.html'
    // templateUrl: 'attendance.component.html'
    ,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AttendanceComponent extends AbstractComponent implements OnInit {
  pager: Pager<Attendance> = new Pager<Attendance>({ perPage: 10 });
  @ViewChild('picker', { static: true }) picker: MatDatepicker<Date>;
  isPicker: boolean = false;
  showDetails: boolean = false;
  showDetailsContact: boolean = false;
  defaultValue: string = '';

  listObservable: Observable<Pager<Attendance>>;
  private termOfSearch: Subject<string> = new Subject<string>();

  attendancesPending: number = 0;
  attendancesInProgress: number = 0;
  attendancesClosed: number = 0;
  attendancesOvertime: number = 0;
  attendancesPaused: number = 0;

  listStatusAttendances: Array<AttendanceStatusEnum> = new Array<AttendanceStatusEnum>(
    AttendanceStatusEnum.OVERTIME,
    AttendanceStatusEnum.PENDING,
    AttendanceStatusEnum.IN_PROGRESS,
    AttendanceStatusEnum.PAUSED,
    AttendanceStatusEnum.CLOSED
  );
  listDepartment: Array<Department> = new Array<Department>();
  listDepartmentChannel: Array<Department> = new Array<Department>();
  listChannel: Array<Channel> = new Array<Channel>();
  listUser: Array<User> = new Array<User>();
  listTeam: Array<Team> = new Array<Team>();
  listTeamChannel: Array<Team> = new Array<Team>();
  attendantsByTeam: Array<any> = new Array<any>();

  labelStatus: string = this.t('attendance.panelList.columnStatus');
  labelDepartment: string = this.t('attendance.panelList.labelDepartments');
  labelChannel: string = this.t('attendance.panelList.labelChannels');
  labelUser: string = this.t('attendance.panelList.agent');
  labelPeriod: string = this.t('attendance.panelList.selectPeriod');

  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    private attendanceService: AttendanceService,
    private sidebarService: SidebarService,
    private departmentService: DepartmentService,
    private channelService: ChannelService,
    private userService: UserService,
    private teamService: TeamService,
  ) {
    super(injector);
    this.loadingContent = true;
    this.createForm();
    this.cacheKey = CacheKeyEnum.ATTENDANCE_LIST;
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    const sources: { [key: string]: Observable<any> } = {
      channels: this.channelService.getList(),
    };

    if (this.isAdmin()) {
      sources['users'] = this.userService.getList();
      sources['departments'] = this.departmentService.getList();
    } else if (this.isSupervisor() || this.isAttendant()) {
      sources['teams'] = this.teamService.getList();
    }

    forkJoin(sources).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.listChannel = data['channels'];

        if (data['users']) {
          this.listUser = data['users'];
        }
        if (data['departments']) {
          this.listDepartment = data['departments'];
        }
        if (data['teams']) {
          this.listTeam = (data['teams'] as Array<Team>)
            .filter((team: Team) => {
              const userId = this.getCurrentUserUser()._id;
              const isAgent = team.agents.some((agent: User) => agent._id === userId);
              const isSupervisor = team.supervisors.some((supervisor: User) => supervisor._id === userId);
              return isAgent || isSupervisor;
            })
            .filter((team: Team) => team.department && team.department.removed !== true);
        }

        const cached = this.restoreState<Attendance>();
        if (!cached) {
          this.getList();
          return;
        }

        this.pager = cached.pager;
        this.searchString = cached.search ?? '';
        this.searchParams = cached.params ?? {};
        this.loadingContent = false;
        this.resolveFiltersCache();
        // Verificar se realmente precisa ser chamado todas as vezes
        // this.getAttendancesByStatus();
        this.setupObservableSearch();
      },
    });
  }

  openPicker(): void {
    this.isPicker = true;
    setTimeout(() => {
      this.picker.open();
    }, 300);
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      channel: [null],
      user: [null],
      dateRange: this.formBuilder.group({
        dateStart: [null],
        dateEnd: [null],
      }),
      department: [null],
      status: [null],
    });
  }

  getList() {
    this.loadingContent = true;
    this.searchParams = {};

    const status = this.formGroup.get('status')?.value;
    if (status) {
      this.searchParams['status'] = status;
    }

    const department = this.formGroup.get('department')?.value;
    if (department) {
      this.searchParams['department'] = department;
    }

    const channel = this.formGroup.get('channel')?.value;
    if (channel) {
      this.searchParams['channel'] = channel;
    }

    const user = this.formGroup.get('user')?.value;
    if (user) {
      this.searchParams['user'] = user;
    }

    const dateRange = this.formGroup.get('dateRange').value;
    if (dateRange?.dateStart && dateRange?.dateEnd) {
      this.searchParams['dateStart'] = dateRange.dateStart.getTime();
      this.searchParams['dateEnd'] = dateRange.dateEnd.getTime();
    }

    this.getAttendancesByStatus();
    this.attendanceService.getPagerToAttendance(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Attendance>) => {
          this.pager = pager;
          this.setupObservableSearch();
          this.loadingContent = false;
          this.persistListState(this.pager, this.searchString, this.searchParams);
        },
        complete: () => this.loadingContent = false,
      });
  }

  setupObservableSearch() {
    this.listObservable = this.termOfSearch.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(term => {
        this.searchString = term;
        return this.attendanceService.getPagerToAttendance(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      }));
    this.listObservable.subscribe({
      next: (pager: Pager<Attendance>) => {
        this.pager = pager;
        this.loadingContent = false;
        this.persistListState(this.pager, this.searchString, this.searchParams);
      },
      complete: () => this.loadingContent = false,
    });
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  delete(attendance: Attendance) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.attendanceService.delete(attendance._id).subscribe({
          next: () => {
            this.getList();
            this.alertService.success();
          },
          error: err => this.alertService.error(err.error.message)
        })
      }
    });
  }

  search(term: string) {
    this.pager.page = 1
    this.loadingContent = true;
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  changeShowDetails() {
    this.showDetails = !this.showDetails;
  }

  changeShowDetailsContact() {
    this.showDetailsContact = !this.showDetailsContact;
  }

  closeMenu() {
    this.sidebarService.setExpandedState(false);
  }

  private getAttendancesByStatus(): void {
    this.searchParams['closedLast24hours'] = false;
    this.attendanceService.getCountAttendancesByStatus(this.searchString, this.searchParams).subscribe({
      next: (counts: Array<{ status: AttendanceStatusEnum, count: number }>) => {
        this.attendancesOvertime = counts?.find((value) => value.status === AttendanceStatusEnum.OVERTIME)?.count || 0;
        this.attendancesPending = counts?.find((value) => value.status === AttendanceStatusEnum.PENDING)?.count || 0;
        this.attendancesInProgress = counts?.find((value) => value.status === AttendanceStatusEnum.IN_PROGRESS)?.count || 0;
        this.attendancesPaused = counts?.find((value) => value.status === AttendanceStatusEnum.PAUSED)?.count || 0;
        this.attendancesClosed = counts?.find((value) => value.status === AttendanceStatusEnum.CLOSED)?.count || 0;
      },
    });
  }

  openAssignedUserTransferModal(attendance: Attendance) {
    const dialogRef = this.dialog.open(AssignedUserComponent, {
      width: '600px',
      data: attendance
    });

    dialogRef.afterClosed().subscribe(result => {
      if(result) this.getList()
    });
  }

  showTransferButton(attendance: Attendance): boolean {
    return attendance.status === AttendanceStatusEnum.PENDING
      || attendance.status === AttendanceStatusEnum.IN_PROGRESS
      || attendance.status === AttendanceStatusEnum.OVERTIME
      || attendance.status === AttendanceStatusEnum.PAUSED;
  }

  showDetailsAttendanceHitory(item: Attendance) {
    this.dialog.open(AttendanceHistoryComponent, {
      width: '90vw',
      height: '95vh',
      data: <AttendanceHistoryData>{
        openAttendanceHistoryUnique: true,
        idContact: item.contact._id,
        idAttendance: item._id,
      }
    });
  }

  searchByStatus(status: AttendanceStatusEnum): void {
    if (status) {
      this.labelStatus = status === AttendanceStatusEnum.OVERTIME ? this.t('attendance.panelList.overtime') :
        status === AttendanceStatusEnum.PENDING ? this.t('attendance.panelList.pending') : status === AttendanceStatusEnum.IN_PROGRESS ?
          this.t('attendance.panelList.statusInProgressFilter') : status === AttendanceStatusEnum.PAUSED ? this.t('attendance.panelList.paused') : status === AttendanceStatusEnum.CLOSED ?
            this.t('attendance.panelList.closed') : '-';
      this.formGroup.get('status').setValue(status);
    } else {
      this.labelStatus = this.t('attendance.panelList.columnStatus');
      this.formGroup.get('status').setValue(null);
    }
    this.pager.page = 1;
    this.getList();
  }

  searchByDepartment(department: Department): void {
    if (department) {
      this.labelDepartment = department.name;
      this.formGroup.get('department').setValue(department._id);
      if (this.isAdmin()) {
        this.teamService.getList().pipe(takeUntil(this.destroy$)).subscribe((teams: Array<Team>) => {
          this.attendantsByTeam = teams
            .filter((team: Team) => team.department._id === department._id && team.agents?.length)
            .flatMap((team: Team) => team.agents);
        });
      } else if ((this.isSupervisor() || this.isAttendant())) {
        this.attendantsByTeam = this.listTeam
          .filter((team: Team) => team.department._id === department?._id && team.agents?.length)
          .flatMap((team: Team) => team.agents);
      }
    } else {
      this.labelDepartment = this.t('attendance.panelList.labelDepartments');
      this.formGroup.get('department').setValue(null);
    }
    this.pager.page = 1;
    this.getList();
  }

  searchByChannel(channel: Channel): void {
    if (channel) {
      this.labelChannel = channel.name;
      this.formGroup.get('channel').setValue(channel._id);
      if (this.listDepartment) {
        this.listDepartmentChannel = this.listDepartment.filter((dept: Department) => dept.channel?.toString() === channel._id);
      }
      if (this.listTeam) {
        this.listTeamChannel = this.listTeam.filter((team: Team) => team.department.channel?.toString() === channel._id);
      }
    } else {
      this.labelChannel = this.t('attendance.panelList.labelChannels');
      this.formGroup.get('channel').setValue(null);
      this.listDepartmentChannel = [];
      this.listTeamChannel = [];
    }
    this.pager.page = 1;
    this.getList();
  }

  searchByUser(user: User): void {
    if (user) {
      this.labelUser = user.name;
      this.formGroup.get('user').setValue(user._id);
    } else {
      this.labelUser = this.t('attendance.panelList.responsible');
      this.formGroup.get('user').setValue(null);
    }
    this.pager.page = 1;
    this.getList();
  }

  getDepartmentName(idDepartment: unknown): string {
    if (idDepartment) {
      return this.listDepartment
        .filter((dept: Department) => dept._id === idDepartment)
        .map((dept: Department) => dept.name)
        .reduce((name: string, current: string) => current, '-');
    } else {
      return '-';
    }
  }

  getNameOfUser(idUser: unknown): string {
    if (idUser) {
      return this.listUser
        .filter((user: User) => user._id === idUser)
        .map((user: User) => user.name)
        .reduce((name: string, current: string) => current, '-');
    } else {
      return '-';
    }
  }

  getImageUser(idUser: unknown): string {
    if (idUser) {
      return this.listUser.filter((user: User) => user._id === idUser)
        .map((user: User) => user.image)
        .reduce((image: string, current: string) => current, '-');
    } else {
      return '-';
    }
  }

  getContactName(): string {
    return null;
  }

  private resolveChannel(channel: string | Channel | null | undefined): Channel | undefined {
    return typeof channel === 'string' ? this.listChannel.find(x => x._id === channel) : channel || undefined;
  }
  getChannelType(channel: string | Channel | null | undefined): string {
    return this.resolveChannel(channel)?.type ?? '-';
  }
  getChannelName(channel: string | Channel | null | undefined): string {
    return this.resolveChannel(channel)?.name ?? '-';
  }
  isHybridChannel(channel: string | Channel | null | undefined): boolean {
    return isHybridChannel(this.resolveChannel(channel));
  }

  onPeriodChange(event: any) {
    const today = new Date();
    switch (event.value) {
      case '1':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(),
          dateEnd: new Date()
        });
        break;
      case '7':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(today.setDate(today.getDate() - 7)),
          dateEnd: new Date()
        });
        break;
      case '15':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(today.setDate(today.getDate() - 15)),
          dateEnd: new Date()
        });
        break;
      case '30':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(today.setDate(today.getDate() - 30)),
          dateEnd: new Date()
        });
        break;
      case '60':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(today.setDate(today.getDate() - 60)),
          dateEnd: new Date()
        });
        break;
    }
    this.picker.close();
    this.getList();
  }

  translateStatus(status: AttendanceStatusEnum): string {
    switch (status) {
      case AttendanceStatusEnum.IN_PROGRESS:
        return this.t('attendance.panelList.inProgress');
      case AttendanceStatusEnum.PENDING:
        return this.t('attendance.panelList.statusPending');
      case AttendanceStatusEnum.OVERTIME:
        return this.t('attendance.panelList.overtime');
      case AttendanceStatusEnum.PAUSED:
        return this.t('attendance.panelList.paused');
      case AttendanceStatusEnum.CLOSED:
        return this.t('attendance.panelList.statusClosed');
      default:
        return this.t('attendance.panelList.statusUnknown');
    }
  }
  private resolveFiltersCache(): void {
    if (!this.searchParams || !Object.keys(this.searchParams).length) return;

    if (this.searchParams['status']) {
      const status = this.searchParams['status'] as AttendanceStatusEnum;
      this.labelStatus = status === AttendanceStatusEnum.OVERTIME ? this.t('attendance.panelList.overtime') :
        status === AttendanceStatusEnum.PENDING ? this.t('attendance.panelList.pending') :
          status === AttendanceStatusEnum.IN_PROGRESS ? this.t('attendance.panelList.statusInProgressFilter') :
            status === AttendanceStatusEnum.PAUSED ? this.t('attendance.panelList.paused') :
              status === AttendanceStatusEnum.CLOSED ? this.t('attendance.panelList.closed') : this.t('attendance.panelList.columnStatus');
      this.formGroup.get('status')?.setValue(status, { emitEvent: false });
    } else {
      this.labelStatus = this.t('attendance.panelList.columnStatus');
    }

    if (this.searchParams['channel']) {
      this.formGroup.get('channel')?.setValue(this.searchParams['channel'], { emitEvent: false });
      const channel = this.listChannel.find(c => c._id === this.searchParams['channel']);
      this.labelChannel = channel?.name ?? this.t('attendance.panelList.labelChannels');
      if (channel) {
        this.listDepartmentChannel = this.listDepartment
          .filter((dept: Department) => dept.channel?.toString() === channel._id);
        this.listTeamChannel = this.listTeam
          .filter((team: Team) => team.department.channel?.toString() === channel._id);
      }
    } else {
      this.labelChannel = this.t('attendance.panelList.labelChannels');
    }

    if (this.searchParams['department']) {
      this.formGroup.get('department')?.setValue(this.searchParams['department'], { emitEvent: false });
      const dept = this.listDepartment.find(d => d._id === this.searchParams['department']);
      this.labelDepartment = dept?.name ?? this.t('attendance.panelList.labelDepartments');
    } else {
      this.labelDepartment = this.t('attendance.panelList.labelDepartments');
    }

    if (this.searchParams['user']) {
      this.formGroup.get('user')?.setValue(this.searchParams['user'], { emitEvent: false });
      const user = this.listUser.find(u => u._id === this.searchParams['user']);
      this.labelUser = user?.name ?? this.t('attendance.panelList.agent');
    } else {
      this.labelUser = this.t('attendance.panelList.agent');
    }

    if (this.searchParams['dateStart'] && this.searchParams['dateEnd']) {
      this.formGroup.get('dateRange')?.setValue({
        dateStart: new Date(this.searchParams['dateStart'] as number),
        dateEnd: new Date(this.searchParams['dateEnd'] as number),
      }, { emitEvent: false });
    }

  }
}
